import React from 'react';
import {
  FormControlLabel,
  Checkbox,
  FormControl,
  FormGroup,
  FormHelperText,
  Box,
} from '@mui/material';

/**
 * FormCheckbox - Reusable checkbox component (single or multiple)
 * @param {Object} props
 * @param {string} props.name - Field name attribute
 * @param {string} [props.label] - Field label for single checkbox
 * @param {boolean|Array} props.value - Current value (boolean for single, array for multi)
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.onBlur] - Blur handler
 * @param {Array} [props.options] - Array of options for multi-checkbox [{label: '', value: ''}, ...]
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.error] - Show error state
 * @param {string} [props.helperText] - Helper/error text
 * @param {string} [props.color] - Checkbox color (primary, secondary, error, warning, info, success)
 * @param {boolean} [props.row] - Display options in a row (for multi-checkbox)
 * @param {string} [props.className] - Additional CSS class
 * @param {Object} [props.sx] - MUI sx prop for styling
 * @param {Object} [props.rest] - Other Checkbox props
 */
const FormCheckbox = React.forwardRef((props, ref) => {
  const {
    name,
    label,
    value = false,
    onChange,
    onBlur,
    options = [],
    required = false,
    disabled = false,
    error = false,
    helperText = '',
    color = 'primary',
    row = false,
    className,
    sx = {},
    ...rest
  } = props;

  // Single checkbox
  if (options.length === 0) {
    return (
      <Box className={className}>
        <FormControlLabel
          ref={ref}
          name={name}
          disabled={disabled}
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              color={color}
              sx={{ "&.Mui-disabled": { color: "action.disabled" } }}
              {...rest}
            />
          }
          label={label}
          sx={sx}
        />
        {helperText && error && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </Box>
    );
  }

  // Multiple checkboxes
  const selectedValues = Array.isArray(value) ? value : [];

  const handleCheckChange = (optionValue) => (event) => {
    const checked = event.target.checked;
    let newValue = [...selectedValues];

    if (checked) {
      newValue.push(optionValue);
    } else {
      newValue = newValue.filter((v) => v !== optionValue);
    }

    // Create a synthetic event-like object
    const syntheticEvent = {
      target: {
        name: name,
        value: newValue,
        type: 'checkbox',
      },
    };

    onChange?.(syntheticEvent);
  };

  return (
    <Box className={className}>
      <FormControl error={error} fullWidth>
        <FormGroup row={row} sx={sx}>
          {options.map((option) => (
            <FormControlLabel
              ref={ref}
              key={option.value}
              disabled={disabled || option.disabled}
              control={
                <Checkbox
                  name={name}
                  checked={selectedValues.includes(option.value)}
                  onChange={handleCheckChange(option.value)}
                  onBlur={onBlur}
                  disabled={disabled || option.disabled}
                  color={color}
                  sx={{ "&.Mui-disabled": { color: "action.disabled" } }}
                  {...rest}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
});

FormCheckbox.displayName = 'FormCheckbox';

export default FormCheckbox;
